const PLACEHOLDER_MASK_REGEX = /9/g;
const ESCAPED_CHARACTER_REGEX = /\\/g;

/**
 * Returns a new string with the last character removed
 * @param {string} [data] string from where we want to remove last character
 * @returns {string} string with the last character removed
 */
const removeLastCharacter = (data) => data.slice(0, -1);

/**
 * Returns the formatted mask to be displayed to the user
 * @param {string} [mask] value of the mask used by the component
 * @param {string} [maskPlaceholder] placeholder that will be displayed to the user as placeholder to enter character
 * @returns {string} mask formatted to use mask placeholder instead of the ones to define the mask
 */
export const getFormattedMask = (mask, maskPlaceholder) => {
  let escapeFound = false;
  return mask
    .split('')
    .map((char, index, arr) => {
      if (char.match(ESCAPED_CHARACTER_REGEX) && arr[index + 1]?.match(PLACEHOLDER_MASK_REGEX)) {
        escapeFound = true;
        return '';
      }
      if (escapeFound) {
        escapeFound = false;
        return char;
      }
      return char.replace(new RegExp(PLACEHOLDER_MASK_REGEX), maskPlaceholder);
    })
    .join('');
};
/**
 * Returns if user is adding a new character in the input mask
 * @param {string} [currentInputValue] string that contains the input value before being modified
 * @param {string} [newInputValue] string that contains the input value after being modified
 * @returns {boolean} indicates if user is adding a new character in the input mask
 */
export const isAddingACharacter = (currentInputValue, newInputValue) => currentInputValue.length < newInputValue.length;

/**
 * Returns if user is modifying data that was already added or if user is adding new character at the end of the existing value
 * @param {string} [currentInputValue] string that contains the input value before being modified
 * @param {string} [newValue] string that contains the input value after being modified
 * @returns {boolean} indicates if user is modifying data that was already added
 */
export const isModifyingExistingData = (currentInputValue, newValue) => !newValue.startsWith(currentInputValue);

/**
 * Returns a value from which characters have been removed until a valid position is found
 * @param {number} [currentPosition] position where we want to start
 * @param {string} [initialValue] string containing value that we want to modify
 * @param {(currentPostition: number) => boolean} [isValidPosition] function that returns a boolean indicating if current position can be edited
 * @returns {string} returns a value from which characters have been removed until a valid position is found
 */
export const removeCharactersUntilValidPosition = (currentPosition, initialValue, isValidPosition) => {
  let result = initialValue;
  let position = currentPosition;
  while (position !== 0) {
    result = removeLastCharacter(result);
    position--;
    if (isValidPosition(position)) {
      break;
    }
  }
  return result;
};

/**
 * Returns the extracted value from the mask, removing the placeholders and separators
 * @param {string} [value] value from where we want to extract mask
 * @param {string} [mask] mask value with placeholder applied
 * @param {string} [maskPlaceholder] placeholder used in the mask passed as argument
 * @returns {string} extracted value from the mask, removing the placeholders and separators
 */
export const extractMaskFromValue = (value, mask, maskPlaceholder) => {
  let result = '';
  [...value].forEach((character, index) => {
    if (mask[index] === maskPlaceholder) {
      result += character;
    }
  });
  return result;
};

/**
 * Returns if value is valid for the current mask
 * @param {string} [value] value from where we want to extract mask
 * @param {string} [mask] mask value with placeholder applied
 * @param {string} [maskPlaceholder] placeholder used in the mask passed as argument
 * @returns {boolean} indicates if value is valid for the current mask
 */
const isValidValueForMask = (value, mask, maskPlaceholder) => value.length <= mask.split(maskPlaceholder).length - 1;

/**
 * Returns the value with the mask applied
 * @param {string} [value] value from where we want to extract mask
 * @param {string} [mask] mask value with placeholder applied
 * @param {string} [maskPlaceholder] placeholder used in the mask passed as argument
 * @returns {string} the value with the mask applied
 */
export const getValueWithMask = (value, mask, maskPlaceholder) => {
  if (!isValidValueForMask(value, mask, maskPlaceholder)) {
    return '';
  }
  const pattern = new RegExp(maskPlaceholder);
  const chars = value.split('');
  let count = 0;
  let formatted = '';

  for (let i = 0; i < mask.length; i++) {
    const maskCharacter = mask[i];
    if (chars[count]) {
      if (pattern.test(maskCharacter)) {
        formatted += chars[count];
        count++;
      } else {
        formatted += maskCharacter;
      }
    } else if (mask && mask.split('')[i]) {
      formatted += mask.split('')[i];
    }
  }
  const firstPlaceholder = formatted.indexOf(maskPlaceholder);
  return firstPlaceholder !== -1 ? formatted.slice(0, firstPlaceholder) : formatted;
};
